import {useState} from "react";
import LeftSectionBlock from "../../components/LeftSectionBlock";
import RightSectionBlock from "../../components/RightSectionBlock";
import ResponsiveImageMapDNA from "../../components/ResponsiveImageMapDNA";
import DNAVideo from "../../assets/videos/iCXeed-DNA.mp4";
import DNABG from "../../assets/images/dnabg.svg";
import HoverBlock from "../../components/HoverBlock";
import Accordion from "../../components/Accordion";

import AngasAudio from "../../assets/sounds/Angas.mp3";
import DiskarteAudio from "../../assets/sounds/Diskarte.mp3";
import TalinoAudio from "../../assets/sounds/Talino.mp3";
import TiyagaAudio from "../../assets/sounds/Tiyaga.mp3";
import KusaAudio from "../../assets/sounds/Kusa.mp3";
import KulitAudio from "../../assets/sounds/Kulit.mp3";
import PusoAudio from "../../assets/sounds/Puso.mp3";

const areas = [
  {
    shape: "circle",
    coords: "134,457,69",
    alt: "Angas",
    color: "rgba(0, 0, 255, 0)",
    className: "angas-bubble-hover-blk",
  },
  {
    shape: "circle",
    coords: "215,315,76",
    alt: "Diskarte",
    color: "rgba(0, 0, 255, 0)",
    className: "diskarte-bubble-hover-blk",
  },
  {
    shape: "circle",
    coords: "298,170,78",
    alt: "Talino",
    color: "rgba(0, 0, 255, 0)",
    className: "talino-bubble-hover-blk",
  },
  {
    shape: "circle",
    coords: "464,97,82",
    alt: "Tiyaga",
    color: "rgba(0, 0, 255, 0)",
    className: "tiyaga-bubble-hover-blk",
  },
  {
    shape: "circle",
    coords: "645,112,70",
    alt: "Kusa",
    color: "rgba(0, 0, 255, 0)",
    className: "kusa-bubble-hover-blk",
  },
  {
    shape: "circle",
    coords: "784,183,70",
    alt: "Kulit",
    color: "rgba(0, 0, 255, 0)",
    className: "kulit-bubble-hover-blk",
  },
  {
    shape: "circle",
    coords: "928,287,73",
    alt: "Puso",
    color: "rgba(0, 0, 255, 0)",
    className: "puso-bubble-hover-blk",
  },
];

const onClickSoundHandler = (audioFile: string) => {
  const audio = new Audio(audioFile);
  audio.play();
};

const sections = [
  {
    title: "Angas",
    content: (
      <div className="content-accordion">
        <p>
          Angas [AHNG-us]
          <i
            className="fa-solid fa-volume-high"
            onClick={() => onClickSoundHandler(AngasAudio)}></i>
        </p>
        <p>(Self-Assured Confidence)</p>
        <p>
          Confidence from knowing one's abilities and worth. Commanding
          attention and respect with boldness and assertiveness.
        </p>
        <p>Angas in Action:</p>
        <ul>
          <li>
            <p>
              Boldly speaks up and contributes constructively to discussions.
            </p>
          </li>
          <li>
            <p>Embraces challenges and steps out of comfort zone.</p>
          </li>
          <li>
            <p>Takes ownership of tasks and projects</p>
          </li>
          <li>
            <p>Communicates clearly and directly</p>
          </li>
          <li>
            <p>
              Demonstrates confidence and self-awareness by acknowledging both
              strengths and vulnerabilities. Seeks help when necessary.
            </p>
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "Diskarte",
    content: (
      <div className="content-accordion">
        <p>
          Diskarte [dees-KAR-teh]
          <i
            className="fa-solid fa-volume-high"
            onClick={() => onClickSoundHandler(DiskarteAudio)}></i>
        </p>
        <p>(Resourceful & Practical Ingenuity)</p>
        <p>
          Diskarte is our embodiment of resourcefulness and practical wisdom.
          It's about employing creative strategies and a street-smart approach
          to problem-solving. This value reflects our ability to adapt, think
          quickly, and navigate challenges effectively.
        </p>
        <p>Diskarte in Action:</p>
        <ul>
          <li>
            <p>
              Adaptability and Flexibility: We show adaptability and flexibility
              in various situations, ensuring we meet our objectives even in
              changing circumstances.
            </p>
          </li>
          <li>
            <p>
              Proactive Planning: We use foresight and intuition to anticipate
              consequences and plan ahead, ensuring smooth operations.{" "}
            </p>
          </li>
          <li>
            <p>
              Innovative Problem-Solving: Our resourcefulness allows us to
              navigate challenges with limited options, using unconventional
              approaches to achieve goals.
            </p>
          </li>
          <li>
            <p>
              Building Relationships: We effectively build rapport and networks,
              leveraging these relationships to achieve our objectives.
            </p>
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "Talino",
    content: (
      <div className="content-accordion">
        <p>
          Talino [tah-LEE-noh]
          <i
            className="fa-solid fa-volume-high"
            onClick={() => onClickSoundHandler(TalinoAudio)}></i>
        </p>
        <p>(Cognitive Agility)</p>
        <p>
          Talino represents our combination of cognitive agility, depth of
          expertise, and the ability to learn quickly. It's about
          problem-solving, critical thinking, and the ability to grasp complex
          concepts efficiently.
        </p>
        <p>Talino in Action:</p>
        <ul>
          <li>
            <p>
              Critical Thinking: We analyse information critically, identify key
              issues, and make sound judgments, ensuring informed decisions.
            </p>
          </li>
          <li>
            <p>
              Quick Learning: Our team is quick to grasp instructions and retain
              information, enabling us to stay ahead in our field.
            </p>
          </li>
          <li>
            <p>
              Continuous Improvement: We continuously pursue opportunities to
              expand our expertise, staying updated on industry trends and best
              practices.
            </p>
          </li>
          <li>
            <p>
              Effective Communication: We leverage new insights to innovate and
              communicate substantive ideas clearly and persuasively.
            </p>
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "Tiyaga",
    content: (
      <div className="content-accordion">
        <p>
          Tiyaga: [cha-GAH]
          <i
            className="fa-solid fa-volume-high"
            onClick={() => onClickSoundHandler(TiyagaAudio)}></i>
        </p>
        <p>(Grit & Tenacity)</p>
        <p>
          Tiyaga embodies the grit, tenacity, and patience crucial for achieving
          long-term goals. It's about staying composed in adversity and being
          committed to completing projects, no matter how challenging.
        </p>
        <p>Tiyaga in Action:</p>
        <ul>
          <li>
            <p>
              Long-Term Focus: We maintain focus on long-term goals despite
              short-term setbacks, ensuring progress is steady and consistent.
            </p>
          </li>
          <li>
            <p>
              Diligence: Our team demonstrates diligence and reliability in
              completing tasks and tackling challenges persistently.
            </p>
          </li>
          <li>
            <p>
              Attention to Detail: We pay meticulous attention to detail,
              ensuring thorough understanding and consideration of all factors
              in our work.
            </p>
          </li>
          <li>
            <p>
              Composure Under Pressure: We remain composed and level-headed
              under pressure, ensuring that our performance remains consistent
              and high-quality.
            </p>
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "Kusa",
    content: (
      <div className="content-accordion">
        <p>
          Kusa: [KOO-sah]
          <i
            className="fa-solid fa-volume-high"
            onClick={() => onClickSoundHandler(KusaAudio)}></i>
        </p>
        <p>(Independent Initiative)</p>
        <p>
          Kusa reflects our self-initiative and taking proactive action
          independently. Reflects a sense of self- motivation, personal
          responsibility, and the drive to contribute positively and make a
          difference.
        </p>
        <p>Kusa in Action:</p>
        <ul>
          <li>
            <p>
              Proactive Contribution: We take the initiative to act
              independently and responsibly, actively seeking ways to add value.
            </p>
          </li>
          <li>
            <p>
              Ownership of Tasks: Our team takes ownership of tasks and
              responsibilities, ensuring that projects are completed efficiently
              and effectively.
            </p>
          </li>
          <li>
            <p>
              Active Involvement: We identify areas where our skills can
              contribute and involve ourselves in relevant projects, making a
              positive impact.
            </p>
          </li>
          <li>
            <p>
              Responsibility: We act willingly and proactively, without the need
              for reminders, in pursuit of organisational goals.
            </p>
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "Kulit",
    content: (
      <div className="content-accordion">
        <p>
          Kulit: [koo-LEET]
          <i
            className="fa-solid fa-volume-high"
            onClick={() => onClickSoundHandler(KulitAudio)}></i>
        </p>
        <p>(Cheerful Persistence & Curiosity)</p>
        <p>
          Kulit is our insatiable curiosity and relentless pursuit of answers.
          It's about maintaining a playful persistence and spreading positivity,
          even in the face of setbacks.
        </p>
        <p>Kulit in Action:</p>
        <ul>
          <li>
            <p>
              Playful Curiosity: We approach tasks with curiosity and an
              innovative mindset, generating fresh ideas and solutions.
            </p>
          </li>
          <li>
            <p>
              Relentless Pursuit: Our team fearlessly pursues desired outcomes,
              persisting until resolution is achieved.
            </p>
          </li>
          <li>
            <p>
              Questioning Norms: We encourage creativity by questioning
              established norms and practices, sparking innovation.
            </p>
          </li>
          <li>
            <p>
              Resilience and Positivity: We bounce back from setbacks with
              resilience, maintaining a cheerful demeanour and inspiring others
              to do the same
            </p>
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "Puso",
    content: (
      <div className="content-accordion">
        <p>
          Puso: [POO-soh]
          <i
            className="fa-solid fa-volume-high"
            onClick={() => onClickSoundHandler(PusoAudio)}></i>
        </p>
        <p>(Heart, Passion, & Purpose)</p>
        <p>
          Puso is the heart of iCXeed, embodying our inner strength, passion,
          and commitment to excellence. It's about leading with empathy,
          purpose, and resilience.
        </p>
        <p>Puso in Action:</p>
        <ul>
          <li>
            <p>
              Empathy and Connection: We demonstrate empathy by considering
              others' viewpoints and experiences in our decisions, fostering
              strong connections.
            </p>
          </li>
          <li>
            <p>
              Passionate Leadership: Our team drives the relentless pursuit of
              goals, facing challenges with courage and embracing learning
              opportunities.
            </p>
          </li>
          <li>
            <p>
              Dedication to Excellence: We are committed to achieving excellence
              in all our work, contributing to the success of the organisation
            </p>
          </li>
          <li>
            <p>
              Resilience: We lead by example, showing inner strength and
              resilience in all our actions and decisions.
            </p>
          </li>
        </ul>
      </div>
    ),
  },
];

const MainContent = ({setShowModal}: any) => {
  return (
    <>
      <div className="our-dna-container-blk ourdna-desktop-blk">
        <ResponsiveImageMapDNA
          src={DNABG}
          alt="DNA"
          mapName="dna"
          areas={areas}
        />

        <HoverBlock
          setShowModal={setShowModal}
          description="Watch our Experts talk about our DNA here"
        />
      </div>
      <div className="ourdna-mobile-blk">
        <img src={DNABG} />

        <HoverBlock
          setShowModal={setShowModal}
          description="Watch our Experts talk about our DNA here"
        />

        <Accordion sections={sections} />
      </div>
    </>
  );
};

const OurDNAPage = () => {
  const [showModal, setShowModal] = useState<boolean>(false);

  const onClickClose = () => {
    if (showModal) {
      setShowModal?.(false);
    }
  };

  return (
    <div className="main-container-blk">
      <div className="main-section-blk">
        {showModal ? (
          <div className="video-modal-blk">
            <div className="modal-content-blk">
              <div className="close-btn-modal">
                <i className="fa-solid fa-xmark" onClick={onClickClose}></i>
              </div>
              <video className="dna-video-blk" controls>
                <source src={DNAVideo} type="video/mp4" />
              </video>
            </div>
          </div>
        ) : null}
        <LeftSectionBlock />
        <RightSectionBlock
          setShowModal={setShowModal}
          title="OUR DNA"
          stringBlurb="At iCXeed, our DNA is the essence of who we are and how we innovate. We've chosen Filipino words to capture the precise spirit of each trait, allowing us to fully embody and communicate these fundamental aspects of our culture. These traits fuel our cognitive business process innovation, driving us to deliver exceptional value and transform customer experiences."
          previousBtn="/our-team"
          nextBtn="/recruitment-process"
          mainContent={<MainContent setShowModal={setShowModal} />}
        />
      </div>
    </div>
  );
};

export default OurDNAPage;
